import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  useNavigate,
  useParams,
} from "react-router-dom";

import {
  API_BASE,
  apiRequest,
} from "./shared";

import {
  L2_ASSIGNEES,
  STEPS,
  TABS,
} from "../LeadDetails/constants";

import {
  displayStatus,
  formatIST,
  getDefaultStep,
  getISTParts,
  getNextRetryAt,
  parseISTInputValue,
  toISTInputValue,
} from "../LeadDetails/utils";

import LeadHeader from "../LeadDetails/components/LeadHeader";

import CompactLeadDetails from "../LeadDetails/components/CompactLeadDetails";

import LeadTabs from "../LeadDetails/components/LeadTabs";

import JourneyHistory from "../LeadDetails/components/JourneyHistory";

import {
  ContactedActions,
  FollowupCompletedActions,
  FollowupDueActions,
  L2Actions,
  PrimaryActions,
  QuietActions,
} from "../LeadDetails/components/ActionCards";

import {
  BookedForm,
  CloseLeadForm,
  CustomerCallbackForm,
  FollowupForm,
  TransferL2Form,
} from "../LeadDetails/components/ActionForms";

import {
  ReadonlyRow,
  StatusNotice,
} from "../LeadDetails/components/UI";

const getNextL2Assignee = (lastAssignedTo) => {
  /*
    No previous L2 assignment:
    start with Soumyajeet.
  */
  if (!lastAssignedTo) {
    return L2_ASSIGNEES[0];
  }

  /*
    Last was Soumyajeet
    → next Debalina
  */
  if (
    lastAssignedTo ===
    L2_ASSIGNEES[0]
  ) {
    return L2_ASSIGNEES[1];
  }

  /*
    Last was Debalina
    → next Soumyajeet
  */
  if (
    lastAssignedTo ===
    L2_ASSIGNEES[1]
  ) {
    return L2_ASSIGNEES[0];
  }

  /*
    Safety fallback
  */
  return L2_ASSIGNEES[0];
};


/* ========================================================================== */
/*                              COMPONENT                                     */
/* ========================================================================== */

export default function LeadDetailsPage() {
  const { leadId } =
    useParams();

  const navigate =
    useNavigate();

  const [lead, setLead] =
    useState(null);

  const [
    activeTab,
    setActiveTab,
  ] = useState(
    TABS.ACTION
  );

  const [
    step,
    setStep,
  ] = useState(
    STEPS.PRIMARY
  );

  const [
    previousStep,
    setPreviousStep,
  ] = useState(
    STEPS.PRIMARY
  );

  /*
    Explicit follow-up mode.

    normal = L1
    l2     = L2 can decide:
             self OR assign to L1
  */

  const [
    followupMode,
    setFollowupMode,
  ] = useState(
    "normal"
  );

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    updating,
    setUpdating,
  ] = useState(false);

  const [
    message,
    setMessage,
  ] = useState("");

  const [now, setNow] =
    useState(
      Date.now()
    );

  const [
    closeType,
    setCloseType,
  ] = useState(
    "not_interested"
  );

  /* ---------------------------------------------------------------------- */
  /*                                  FORM                                  */
  /* ---------------------------------------------------------------------- */

  const [
    form,
    setForm,
  ] = useState({
    notes: "",

    assignedTo: "",

    followupAt: "",

    followupOwner:
      "self",

    level:
      "level-1",

    price: "",
  });

  /* ---------------------------------------------------------------------- */
  /*                                 CLOCK                                  */
  /* ---------------------------------------------------------------------- */

  useEffect(() => {
    const timer =
      setInterval(() => {
        setNow(
          Date.now()
        );
      }, 1000);

    return () =>
      clearInterval(
        timer
      );
  }, []);

  /* ---------------------------------------------------------------------- */
  /*                               FETCH LEAD                               */
  /* ---------------------------------------------------------------------- */

  const fetchLead =
    useCallback(
      async (
        showLoader = false,
        syncStep = true
      ) => {
        try {
          if (
            showLoader
          ) {
            setLoading(
              true
            );
          }

          const result =
            await apiRequest(
              `${API_BASE}/lead/${leadId}`
            );

          setLead(
            result.data
          );

          if (
            syncStep
          ) {
            setStep(
              getDefaultStep(
                result.data
              )
            );
          }

          return result.data;
        } catch (error) {
          setMessage(
            error.message
          );

          setLead(null);

          return null;
        } finally {
          if (
            showLoader
          ) {
            setLoading(
              false
            );
          }
        }
      },
      [leadId]
    );

  useEffect(() => {
    fetchLead(true);
  }, [fetchLead]);

  /* ---------------------------------------------------------------------- */
  /*                              RESET FORM                                */
  /* ---------------------------------------------------------------------- */

  const resetForm = () => {
    setForm({
      notes: "",

      assignedTo: "",

      followupAt: "",

      followupOwner:
        "self",

      level:
        "level-1",

      price: "",
    });
  };

  /* ---------------------------------------------------------------------- */
  /*                              BASIC STATE                               */
  /* ---------------------------------------------------------------------- */

  const activeCaller =
    lead?.assignedTo ||
    "Unknown";

  const retryCount =
    Number(
      lead?.retryCount ||
      0
    );

  const isLevel2 =
    lead?.level ===
    "level-2";

  /* ---------------------------------------------------------------------- */
  /*                               CLOSED                                   */
  /* ---------------------------------------------------------------------- */

  const isNotReachable =
    retryCount >= 4 &&
    lead?.currentStatus ===
    "contacted";

  const isClosed =
    [
      "converted",
      "not_interested",
    ].includes(
      lead?.currentStatus
    ) ||
    isNotReachable;

  /* ---------------------------------------------------------------------- */
  /*                           SCHEDULE STATE                               */
  /* ---------------------------------------------------------------------- */

  const scheduledAction =
    useMemo(() => {
      if (
        !lead?.nextFollowupAt
      ) {
        return {
          exists: false,
          active: true,
          before: false,
          missed: false,
          date: null,
        };
      }

      const date =
        new Date(
          lead.nextFollowupAt
        );

      if (
        Number.isNaN(
          date.getTime()
        )
      ) {
        return {
          exists: false,
          active: true,
          before: false,
          missed: false,
          date: null,
        };
      }

      const start =
        new Date(date);

      start.setSeconds(
        0,
        0
      );

      const end =
        start.getTime() +
        60 * 1000;

      return {
        exists: true,

        active:
          now >=
          start.getTime() &&
          now < end,

        before:
          now <
          start.getTime(),

        missed:
          now >= end,

        date,
      };
    }, [
      lead?.nextFollowupAt,
      now,
    ]);

  const canPerformAction =
    !isClosed &&
    (
      !scheduledAction.exists ||
      scheduledAction.active
    );

  const canDial =
    canPerformAction &&
    Boolean(
      lead?.leadDetails
        ?.phone
    );

  /* ---------------------------------------------------------------------- */
  /*                               API UPDATE                               */
  /* ---------------------------------------------------------------------- */

  const saveUpdate =
    async (
      payload,
      {
        successMessage =
        "Lead updated successfully",

        nextStep =
        null,
      } = {}
    ) => {
      try {
        setUpdating(
          true
        );

        setMessage("");

        const result =
          await apiRequest(
            `${API_BASE}/update/${lead.leadId}`,
            {
              method:
                "PUT",

              body:
                JSON.stringify({
                  ...payload,

                  callBy:
                    activeCaller,
                }),
            }
          );

        const updatedLead =
          await fetchLead(
            false,
            false
          );

        setMessage(
          result.message ||
          successMessage
        );

        resetForm();

        if (
          nextStep
        ) {
          setStep(
            nextStep
          );
        } else {
          setStep(
            getDefaultStep(
              updatedLead
            )
          );
        }

        return updatedLead;
      } catch (error) {
        setMessage(
          error.message
        );

        return null;
      } finally {
        setUpdating(
          false
        );
      }
    };

  /* ---------------------------------------------------------------------- */
  /*                            CONTACT MADE                                */
  /* ---------------------------------------------------------------------- */

  /*
    No API call here.

    This just opens the relevant
    next-action card.

    Level 1 → CONTACTED
    Level 2 → L2
  */

  const handleContactMade =
    () => {
      setMessage("");

      resetForm();

      if (isLevel2) {
        setStep(
          STEPS.L2
        );

        return;
      }

      setStep(
        STEPS.CONTACTED
      );
    };

  /* ---------------------------------------------------------------------- */
  /*                      FOLLOW-UP CONTACT MADE                            */
  /* ---------------------------------------------------------------------- */

  const handleFollowupCompleted =
    () => {
      setMessage("");

      resetForm();

      setStep(
        STEPS.FOLLOW_UP_COMPLETE
      );
    };

  /* ---------------------------------------------------------------------- */
  /*                              NO ANSWER                                 */
  /* ---------------------------------------------------------------------- */

 const recordNoAnswer =
  async () => {
    const attempt =
      retryCount + 1;

    const nextRetry =
      getNextRetryAt(
        attempt
      );

    if (attempt >= 4) {
      await saveUpdate(
        {
          outcome:
            "no_answer",

          notes:
            "Not reachable after 4 attempts.",

          nextRetryAt:
            null,
        },
        {
          successMessage:
            "Lead marked not reachable",
        }
      );

      return;
    }

    await saveUpdate(
      {
        outcome:
          "no_answer",

        notes:
          `No answer. Retry ${attempt}/4 scheduled for ${formatIST(
            nextRetry
          )} IST.`,

        nextRetryAt:
          nextRetry.toISOString(),
      },
      {
        successMessage:
          `Retry ${attempt}/4 scheduled`,

        nextStep:
          STEPS.QUIET,
      }
    );
  };

  /* ---------------------------------------------------------------------- */
  /*                      CUSTOMER WILL CALL BACK                           */
  /* ---------------------------------------------------------------------- */

  const openCustomerCallback =
    (fromStep) => {
      setPreviousStep(
        fromStep
      );

      resetForm();

      setStep(
        STEPS.CUSTOMER_CALLBACK
      );
    };

  const submitCustomerCallback =
    async (
      event
    ) => {
      event.preventDefault();

      if (
        !form.notes.trim()
      ) {
        setMessage(
          "Please add a note."
        );

        return;
      }

      await saveUpdate({
        action:
          "status_note",

        currentStatus:
          "contacted",

        notes:
          `Customer will call back. ${form.notes.trim()}`,

        retryCount: 0,

        nextFollowupAt:
          null,
      });
    };

  /* ---------------------------------------------------------------------- */
  /*                          NOT INTERESTED                                */
  /* ---------------------------------------------------------------------- */

  const openNotInterested =
    (fromStep) => {
      setPreviousStep(
        fromStep
      );

      setCloseType(
        "not_interested"
      );

      resetForm();

      setStep(
        STEPS.CLOSE
      );
    };

  /* ---------------------------------------------------------------------- */
  /*                            WRONG NUMBER                                */
  /* ---------------------------------------------------------------------- */

  const openWrongNumber =
    (fromStep) => {
      setPreviousStep(
        fromStep
      );

      setCloseType(
        "wrong_number"
      );

      setForm({
        notes:
          "Wrong number",

        assignedTo: "",

        followupAt: "",

        followupOwner:
          "self",

        level:
          lead.level ||
          "level-1",

        price: "",
      });

      setStep(
        STEPS.CLOSE
      );
    };

  /* ---------------------------------------------------------------------- */
  /*                             CLOSE LEAD                                 */
  /* ---------------------------------------------------------------------- */

  const submitClose =
    async (
      event
    ) => {
      event.preventDefault();

      if (
        !form.notes.trim()
      ) {
        setMessage(
          "Please add a note."
        );

        return;
      }

      const notes =
        closeType ===
          "wrong_number"
          ? `Wrong Number. ${form.notes.trim()}`
          : form.notes.trim();

      await saveUpdate({
        outcome:
          "not_interested",

        notes,

        nextFollowupAt:
          null,
      });
    };

  /* ---------------------------------------------------------------------- */
  /*                           OPEN FOLLOW-UP                               */
  /* ---------------------------------------------------------------------- */

  const openFollowup =
    (fromStep) => {
      setPreviousStep(
        fromStep
      );

      /*
        IMPORTANT:

        We use actual current level.

        Level 2
        → owner selection appears.

        Level 1
        → normal follow-up.
      */

      if (isLevel2) {
        setFollowupMode(
          "l2"
        );

        setForm({
          notes: "",

          assignedTo:
            lead.assignedTo,

          followupAt: "",

          followupOwner:
            "self",

          level:
            "level-2",

          price: "",
        });
      } else {
        setFollowupMode(
          "normal"
        );

        setForm({
          notes: "",

          assignedTo:
            lead.assignedTo ||
            "",

          followupAt: "",

          followupOwner:
            "self",

          level:
            "level-1",

          price: "",
        });
      }

      setStep(
        STEPS.FOLLOW_UP
      );
    };

  /* ---------------------------------------------------------------------- */
  /*                         QUICK FOLLOW-UP                                */
  /* ---------------------------------------------------------------------- */

  const chooseFollowupDays =
    (days) => {
      const parts =
        getISTParts();

      /*
        Quick dates default
        to 10 AM IST.
      */

      const start =
        new Date(
          `${parts.year}-${parts.month}-${parts.day}T10:00:00+05:30`
        );

      const future =
        new Date(
          start.getTime() +
          days *
          24 *
          60 *
          60 *
          1000
        );

      setForm(
        (prev) => ({
          ...prev,

          followupAt:
            toISTInputValue(
              future
            ),
        })
      );
    };

  /* ---------------------------------------------------------------------- */
  /*                          SUBMIT FOLLOW-UP                              */
  /* ---------------------------------------------------------------------- */

  const submitFollowup =
    async (
      event
    ) => {
      event.preventDefault();

      if (
        !form.followupAt
      ) {
        setMessage(
          "Follow-up date is required."
        );

        return;
      }

      const date =
        parseISTInputValue(
          form.followupAt
        );

      if (
        !date ||
        Number.isNaN(
          date.getTime()
        )
      ) {
        setMessage(
          "Invalid follow-up date."
        );

        return;
      }

      if (
        date <=
        new Date()
      ) {
        setMessage(
          "Follow-up must be in the future."
        );

        return;
      }

      /* ================================================================ */
      /* NORMAL LEVEL 1 FOLLOW-UP                                         */
      /* ================================================================ */

      if (
        followupMode ===
        "normal"
      ) {
        await saveUpdate({
          outcome:
            "callback_later",

          notes:
            form.notes.trim(),

          followupAt:
            date.toISOString(),

          nextFollowupAt:
            date.toISOString(),

          assignedTo:
            lead.assignedTo,

          level:
            "level-1",

          retryCount: 0,
        });

        return;
      }

      /* ================================================================ */
      /* LEVEL 2 KEEPS FOLLOW-UP                                          */
      /* ================================================================ */

      if (
        followupMode ===
        "l2" &&
        form.followupOwner ===
        "self"
      ) {
        const notes =
          `Follow-up retained by ${lead.assignedTo}.${form.notes.trim()
            ? ` ${form.notes.trim()}`
            : ""
          }`;

        await saveUpdate({
          outcome:
            "callback_later",

          notes,

          followupAt:
            date.toISOString(),

          nextFollowupAt:
            date.toISOString(),

          assignedTo:
            lead.assignedTo,

          level:
            "level-2",

          retryCount: 0,
        });

        return;
      }

      /* ================================================================ */
      /* LEVEL 2 ASSIGNS FOLLOW-UP TO LEVEL 1                             */
      /* ================================================================ */

      if (
        followupMode ===
        "l2" &&
        form.followupOwner ===
        "level-1"
      ) {
        if (
          !form.assignedTo
        ) {
          setMessage(
            "Please select a Level 1 representative."
          );

          return;
        }

        const oldOwner =
          lead.assignedTo;

        const newOwner =
          form.assignedTo;

        const notes =
          `Follow-up assigned by ${oldOwner} to ${newOwner}.${form.notes.trim()
            ? ` ${form.notes.trim()}`
            : ""
          }`;

        await saveUpdate({
          outcome:
            "callback_later",

          notes,

          followupAt:
            date.toISOString(),

          nextFollowupAt:
            date.toISOString(),

          /*
            Ownership moves
            back to Level 1.
          */

          assignedTo:
            newOwner,

          level:
            "level-1",

          /*
            New owner gets
            fresh retry cycle.
          */

          retryCount: 0,
        });
      }
    };

  /* ---------------------------------------------------------------------- */
  /*                           TRANSFER TO L2                               */
  /* ---------------------------------------------------------------------- */

  const openTransferL2 = () => {
    if (isLevel2) {
      return;
    }

    setPreviousStep(
      STEPS.CONTACTED
    );

    setForm({
      notes: "",
      assignedTo: "",
      followupAt: "",
      followupOwner:
        "self",
      level:
        "level-2",
      price: "",
    });

    setStep(
      STEPS.TRANSFER_L2
    );
  };

  const submitTransferL2 =
    async (event) => {
      event.preventDefault();

      /* ---------------- Safety ---------------- */

      if (isLevel2) {
        setMessage(
          "This lead is already Level 2."
        );

        return;
      }

      /* ---------------- Note ---------------- */

      if (!form.notes.trim()) {
        setMessage(
          "Please add a note before sending to Level 2."
        );

        return;
      }

      try {
        setUpdating(true);
        setMessage("");

        /* ================================================================ */
        /* GET LAST L2 ASSIGNMENT                                           */
        /* ================================================================ */

        const lastAssignment =
          await apiRequest(
            `${API_BASE}/last-l2-assignee`
          );

        const lastAssignedTo =
          lastAssignment?.data
            ?.lastAssignedTo ||
          null;

        /* ================================================================ */
        /* CALCULATE NEXT L2 OWNER                                          */
        /* ================================================================ */

        const newL2Assignee =
          getNextL2Assignee(
            lastAssignedTo
          );

        const oldOwner =
          activeCaller;

        /* ================================================================ */
        /* TRANSFER LEAD                                                    */
        /* ================================================================ */

        const result =
          await apiRequest(
            `${API_BASE}/update/${lead.leadId}`,
            {
              method: "PUT",

              body: JSON.stringify({
                outcome:
                  "transferred",

                /*
                  Actual new owner.
                */
                assignedTo:
                  newL2Assignee,

                level:
                  "level-2",

                retryCount: 0,

                nextFollowupAt:
                  null,

                callBy:
                  oldOwner,

                notes:
                  `Transferred from ${oldOwner} to ${newL2Assignee}. ${form.notes.trim()}`,
              }),
            }
          );

        /* ================================================================ */
        /* REFRESH LEAD                                                     */
        /* ================================================================ */

        const updatedLead =
          await fetchLead(
            false,
            false
          );


        try {
          const n8nResponse = await fetch(
            "https://n8n-automation-n8n.zmsjji.easypanel.host/webhook/4cfbad85-1617-4436-ad93-381629c88fa4",
            {
              method: "POST",

              headers: {
                "Content-Type":
                  "application/json",
              },

              body: JSON.stringify({
                leadId: updatedLead?.leadId || lead.leadId,
                assignedTo:  updatedLead?.assignedTo || lead.assignedTo || null,
                currentLevel:updatedLead?.level || lead.level,
                lead,
                callBy:activeCaller,
                url:`https://www.admin.silvamethod.in/manage-lead/${lead.leadId}`,
              }),
            }
          );

          if (!n8nResponse.ok) {
            console.error(
              "n8n webhook failed:",
              n8nResponse.status
            );
          }
        } catch (n8nError) {
          console.error(
            "n8n webhook error:",
            n8nError
          );
        }


        setMessage(
          result.message ||
          `Lead assigned to ${newL2Assignee}`
        );

        resetForm();

        setStep(
          getDefaultStep(
            updatedLead
          )
        );
      } catch (error) {
        setMessage(
          error.message ||
          "Unable to transfer lead to Level 2."
        );
      } finally {
        setUpdating(false);
      }
    };

  /* ---------------------------------------------------------------------- */
  /*                                BOOKED                                  */
  /* ---------------------------------------------------------------------- */

  const openBooked =
    () => {
      /*
        Booked currently belongs
        to L2 flow.
      */

      if (!isLevel2) {
        return;
      }

      setPreviousStep(
        STEPS.L2
      );

      resetForm();

      setStep(
        STEPS.BOOKED
      );
    };

  const submitBooked =
    async (
      event
    ) => {
      event.preventDefault();

      const price =
        Number(
          form.price
        );

      if (
        !Number.isFinite(
          price
        ) ||
        price <= 0
      ) {
        setMessage(
          "Please enter a valid booking price."
        );

        return;
      }

      /*
        Note is required.
      */

      if (
        !form.notes.trim()
      ) {
        setMessage(
          "Booking note is required."
        );

        return;
      }

      const notes =
        `Booked by ${activeCaller} at ${formatIST(
          new Date()
        )} IST for ₹${price.toLocaleString(
          "en-IN"
        )}. ${form.notes.trim()}`;

      await saveUpdate({
        outcome:
          "converted",

        notes,

        retryCount: 0,

        nextFollowupAt:
          null,
      });
    };

  /* ---------------------------------------------------------------------- */
  /*                              HISTORY                                   */
  /* ---------------------------------------------------------------------- */

  const journey =
    useMemo(() => {
      if (!lead) {
        return [];
      }

      const items = [];

      /* -------------------------------------------------------------- */
      /* CREATED                                                        */
      /* -------------------------------------------------------------- */

      if (
        lead.createdAt
      ) {
        items.push({
          id:
            "created",

          date:
            new Date(
              lead.createdAt
            ),

          title:
            "Lead Created",

          actor:
            lead.assignedTo,

          notes:
            `Assigned to ${lead.assignedTo ||
            "Unassigned"
            }`,
        });
      }

      /* -------------------------------------------------------------- */
      /* ACTIVITY HISTORY                                               */
      /* -------------------------------------------------------------- */

      (
        lead.activityHistory ||
        []
      ).forEach(
        (
          activity,
          index
        ) => {
          items.push({
            id:
              activity._id ||
              `activity-${index}`,

            date:
              new Date(
                activity.createdAt ||
                activity.date
              ),

            title:
              activity.title ||
              activity.action,

            actor:
              activity.by ||
              activity.actor,

            notes:
              activity.notes,
          });
        }
      );

      /* -------------------------------------------------------------- */
      /* CALL HISTORY                                                   */
      /* -------------------------------------------------------------- */

      (
        lead.callDetails ||
        []
      ).forEach(
        (
          call,
          index
        ) => {
          const notes =
            call.notes || "";

          const lowered =
            notes.toLowerCase();

          let title =
            displayStatus(
              call.outcome
            );

          if (
            call.outcome ===
            "converted"
          ) {
            title =
              "Booked";
          }

          if (
            call.outcome ===
            "transferred"
          ) {
            title =
              "Transferred to Level 2";
          }

          if (
            lowered.includes(
              "wrong number"
            )
          ) {
            title =
              "Wrong Number";
          }

          if (
            lowered.includes(
              "not reachable"
            )
          ) {
            title =
              "Not Reachable";
          }

          if (
            lowered.includes(
              "customer will call back"
            )
          ) {
            title =
              "Customer Will Call Back";
          }

          if (
            lowered.includes(
              "follow-up assigned by"
            )
          ) {
            title =
              "Follow-up Assigned to Level 1";
          }

          if (
            lowered.includes(
              "follow-up retained by"
            )
          ) {
            title =
              "Level 2 Follow-up Scheduled";
          }

          items.push({
            id:
              call._id ||
              `call-${index}`,

            date:
              new Date(
                call.callTime ||
                lead.updatedAt
              ),

            title,

            actor:
              call.callBy,

            notes,

            duration:
              call.duration,
          });
        }
      );

      /* -------------------------------------------------------------- */
      /* FOLLOW-UP HISTORY                                              */
      /* -------------------------------------------------------------- */

      (
        lead.followupDetails ||
        []
      ).forEach(
        (
          followup,
          index
        ) => {
          items.push({
            id:
              followup._id ||
              `followup-${index}`,

            date:
              new Date(
                followup.followupAt
              ),

            title:
              `Follow-up ${followup.status}`,

            actor:
              followup.followupBy,

            notes:
              followup.notes,
          });
        }
      );

      return items.sort(
        (a, b) =>
          b.date - a.date
      );
    }, [lead]);

  /* ---------------------------------------------------------------------- */
  /*                               LOADING                                  */
  /* ---------------------------------------------------------------------- */

  if (loading) {
    return (
      <SimplePage>
        Loading lead...
      </SimplePage>
    );
  }

  if (!lead) {
    return (
      <SimplePage>
        {message ||
          "Lead not found"}
      </SimplePage>
    );
  }

  /* ---------------------------------------------------------------------- */
  /*                           FOLLOW-UP DUE                                */
  /* ---------------------------------------------------------------------- */

  const isFollowupDue =
    lead.currentStatus ===
    "follow_up" &&
    scheduledAction.exists &&
    scheduledAction.active;

  /* ---------------------------------------------------------------------- */
  /*                            LEVEL-AWARE ACTIONS                         */
  /* ---------------------------------------------------------------------- */

  const renderLevelActions =
    () => {
      /*
        Centralized rule:

        Level 2 always gets
        L2 actions.

        Level 1 gets
        Contacted actions.
      */

      if (isLevel2) {
        return (
          <L2Actions
            lead={lead}

            retryCount={
              retryCount
            }

            updating={
              updating
            }

            onBooked={
              openBooked
            }

            onFollowup={() =>
              openFollowup(
                STEPS.L2
              )
            }

            onNoAnswer={
              recordNoAnswer
            }

            onCustomerCallback={() =>
              openCustomerCallback(
                STEPS.L2
              )
            }

            onNotInterested={() =>
              openNotInterested(
                STEPS.L2
              )
            }
          />
        );
      }

      return (
        <ContactedActions
          lead={lead}

          onTransfer={
            openTransferL2
          }

          onFollowup={() =>
            openFollowup(
              STEPS.CONTACTED
            )
          }

          onCustomerCallback={() =>
            openCustomerCallback(
              STEPS.CONTACTED
            )
          }

          onNotInterested={() =>
            openNotInterested(
              STEPS.CONTACTED
            )
          }
        />
      );
    };

  /* ---------------------------------------------------------------------- */
  /*                              ACTION UI                                 */
  /* ---------------------------------------------------------------------- */

  const renderAction =
    () => {
      /* ================================================================ */
      /* CLOSED                                                           */
      /* ================================================================ */

      if (isClosed) {
        return (
          <StatusNotice
            title={
              lead.currentStatus ===
                "converted"
                ? "Lead Booked"
                : isNotReachable
                  ? "Not Reachable"
                  : "Lead Closed"
            }
            text={
              isNotReachable
                ? "No contact was made after 4 attempts."
                : "This lead is currently closed."
            }
          />
        );
      }

      /* ================================================================ */
      /* SCHEDULE LOCK                                                    */
      /* ================================================================ */

      if (
        scheduledAction.exists &&
        !scheduledAction.active
      ) {
        return (
          <div className="space-y-3">
            <StatusNotice
              title={
                scheduledAction.before
                  ? "Scheduled for Later"
                  : "Scheduled Time Missed"
              }
              text={
                scheduledAction.before
                  ? `Actions unlock at ${formatIST(
                    scheduledAction.date
                  )} IST.`
                  : `Scheduled action time was ${formatIST(
                    scheduledAction.date
                  )} IST.`
              }
            />

            <ReadonlyRow
              label="Assigned To"
              value={
                lead.assignedTo
              }
            />

            <ReadonlyRow
              label="Scheduled"
              value={`${formatIST(
                scheduledAction.date
              )} IST`}
            />
          </div>
        );
      }

      /* ================================================================ */
      /* FOLLOW-UP DUE                                                    */
      /* ================================================================ */

      if (
        isFollowupDue
      ) {
        return (
          <FollowupDueActions
            followupAt={
              lead.nextFollowupAt
            }

            formatDate={
              formatIST
            }

            updating={
              updating
            }

            onContact={
              handleFollowupCompleted
            }

            onNoAnswer={
              recordNoAnswer
            }

            onNotInterested={() =>
              openNotInterested(
                isLevel2
                  ? STEPS.L2
                  : STEPS.CONTACTED
              )
            }

            onCustomerCallback={() =>
              openCustomerCallback(
                isLevel2
                  ? STEPS.L2
                  : STEPS.CONTACTED
              )
            }
          />
        );
      }

      /* ================================================================ */
      /* FOLLOW-UP COMPLETED                                               */
      /* ================================================================ */

      if (
        step ===
        STEPS.FOLLOW_UP_COMPLETE
      ) {
        return (
          <FollowupCompletedActions
            updating={
              updating
            }

            onAnotherFollowup={() =>
              openFollowup(
                STEPS.FOLLOW_UP_COMPLETE
              )
            }

            onNoAnswer={
              recordNoAnswer
            }

            onNotInterested={() =>
              openNotInterested(
                STEPS.FOLLOW_UP_COMPLETE
              )
            }

            onCustomerCallback={() =>
              openCustomerCallback(
                STEPS.FOLLOW_UP_COMPLETE
              )
            }
          />
        );
      }

      /* ================================================================ */
      /* PRIMARY                                                          */
      /* ================================================================ */

      if (
        step ===
        STEPS.PRIMARY
      ) {
        /*
          A Level 2 lead should
          not normally land here.

          But defensive handling:
          if it does, use L2 card.
        */

        if (isLevel2) {
          return renderLevelActions();
        }

        return (
          <PrimaryActions
            updating={
              updating
            }

            onContact={
              handleContactMade
            }

            onNoAnswer={
              recordNoAnswer
            }

            onNotInterested={() =>
              openNotInterested(
                STEPS.PRIMARY
              )
            }

            onWrongNumber={() =>
              openWrongNumber(
                STEPS.PRIMARY
              )
            }
          />
        );
      }

      /* ================================================================ */
      /* CONTACTED                                                        */
      /* ================================================================ */

      if (
        step ===
        STEPS.CONTACTED
      ) {
        /*
          Crucial:

          Even if step accidentally
          becomes CONTACTED,
          Level 2 still receives
          L2 actions.
        */

        return renderLevelActions();
      }

      /* ================================================================ */
      /* RETRY / QUIET                                                    */
      /* ================================================================ */

      if (
        step ===
        STEPS.QUIET
      ) {
        return (
          <QuietActions
            lead={lead}

            retryCount={
              retryCount
            }

            updating={
              updating
            }

            onContact={
              handleContactMade
            }

            onNoAnswer={
              recordNoAnswer
            }

            onCustomerCallback={() =>
              openCustomerCallback(
                STEPS.QUIET
              )
            }

            onWrongNumber={() =>
              openWrongNumber(
                STEPS.QUIET
              )
            }

            onNotInterested={() =>
              openNotInterested(
                STEPS.QUIET
              )
            }
          />
        );
      }

      /* ================================================================ */
      /* FOLLOW-UP FORM                                                   */
      /* ================================================================ */

      if (
        step ===
        STEPS.FOLLOW_UP
      ) {
        return (
          <FollowupForm
            form={form}

            setForm={
              setForm
            }

            updating={
              updating
            }

            onSubmit={
              submitFollowup
            }

            onQuickDate={
              chooseFollowupDays
            }

            followupMode={
              followupMode
            }

            currentAssignee={
              lead.assignedTo
            }

            onCancel={() =>
              setStep(
                previousStep
              )
            }
          />
        );
      }

      /* ================================================================ */
      /* CUSTOMER CALLBACK                                                */
      /* ================================================================ */

      if (
        step ===
        STEPS.CUSTOMER_CALLBACK
      ) {
        return (
          <CustomerCallbackForm
            form={form}

            setForm={
              setForm
            }

            updating={
              updating
            }

            onSubmit={
              submitCustomerCallback
            }

            onCancel={() =>
              setStep(
                previousStep
              )
            }
          />
        );
      }

      /* ================================================================ */
      /* TRANSFER TO L2                                                   */
      /* ================================================================ */

      if (
        step ===
        STEPS.TRANSFER_L2
      ) {
        /*
          Level 2 must never
          see this form.
        */

        if (isLevel2) {
          return renderLevelActions();
        }

        return (
          <TransferL2Form
            form={form}
            setForm={setForm}
            transferredBy={
              activeCaller
            }
            updating={
              updating
            }
            onSubmit={
              submitTransferL2
            }
            onCancel={() =>
              setStep(
                previousStep
              )
            }
          />
        );
      }

      /* ================================================================ */
      /* LEVEL 2                                                          */
      /* ================================================================ */

      if (
        step ===
        STEPS.L2
      ) {
        /*
          If ownership was moved
          back to L1, don't show
          L2 UI anymore.
        */

        if (!isLevel2) {
          return (
            <ContactedActions
              lead={lead}

              onTransfer={
                openTransferL2
              }

              onFollowup={() =>
                openFollowup(
                  STEPS.CONTACTED
                )
              }

              onCustomerCallback={() =>
                openCustomerCallback(
                  STEPS.CONTACTED
                )
              }

              onNotInterested={() =>
                openNotInterested(
                  STEPS.CONTACTED
                )
              }
            />
          );
        }

        return renderLevelActions();
      }

      /* ================================================================ */
      /* CLOSE                                                            */
      /* ================================================================ */

      if (
        step ===
        STEPS.CLOSE
      ) {
        return (
          <CloseLeadForm
            closeType={
              closeType
            }

            form={form}

            setForm={
              setForm
            }

            updating={
              updating
            }

            onSubmit={
              submitClose
            }

            onCancel={() =>
              setStep(
                previousStep
              )
            }
          />
        );
      }

      /* ================================================================ */
      /* BOOKED                                                           */
      /* ================================================================ */

      if (
        step ===
        STEPS.BOOKED
      ) {
        /*
          Only L2 can reach
          Booked in this flow.
        */

        if (!isLevel2) {
          return renderLevelActions();
        }

        return (
          <BookedForm
            form={form}

            setForm={
              setForm
            }

            bookedBy={
              activeCaller
            }

            bookedAt={`${formatIST(
              new Date()
            )} IST`}

            updating={
              updating
            }

            onSubmit={
              submitBooked
            }

            onCancel={() =>
              setStep(
                previousStep
              )
            }
          />
        );
      }

      return null;
    };

  /* ---------------------------------------------------------------------- */
  /*                                  UI                                    */
  /* ---------------------------------------------------------------------- */

  return (
    <main className="min-h-screen bg-slate-50 text-slate-900">
      <div className="mx-auto max-w-5xl p-4 sm:p-6">
        {/* <button
          type="button"
          onClick={() =>
            navigate(
              "/manage-lead"
            )
          }
          className="mb-4 text-sm font-medium text-slate-600 transition hover:text-slate-900"
        >
          ← Back to Leads
        </button> */}

        <div className="mx-auto max-w-2xl rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
          <LeadHeader
            lead={lead}

            canDial={
              canDial
            }

            scheduledAction={
              scheduledAction
            }

            isNotReachable={
              isNotReachable
            }
          />

          <CompactLeadDetails
            lead={lead}
          />

          <LeadTabs
            activeTab={
              activeTab
            }

            onChange={
              setActiveTab
            }

            historyCount={
              journey.length
            }
          />

          {message && (
            <div className="mt-3 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">
              {message}
            </div>
          )}

          {activeTab ===
            TABS.ACTION && (
              <div className="mt-4">
                {renderAction()}
              </div>
            )}

          {activeTab ===
            TABS.HISTORY && (
              <div className="mt-4">
                <JourneyHistory
                  journey={
                    journey
                  }
                />
              </div>
            )}
        </div>
      </div>
    </main>
  );
}

/* ========================================================================== */
/*                              LOADING PAGE                                  */
/* ========================================================================== */

function SimplePage({
  children,
}) {
  return (
    <main className="min-h-screen bg-slate-50 p-6">
      <div className="mx-auto max-w-2xl rounded-2xl border border-slate-200 bg-white p-10 text-center text-sm text-slate-500">
        {children}
      </div>
    </main>
  );
}